import { useCallback, useEffect, useState } from "react";
import type { Story } from "@/mocks/stories";
import { stories } from "@/mocks/stories";
import { faqs } from "@/mocks/fun";
import Header from "./components/Header";
import Hero from "./components/Hero";
import Ticker from "./components/Ticker";
import Feed from "./components/Feed";
import Featured from "./components/Featured";
import QuoteWall from "./components/QuoteWall";
import MemeGallery from "./components/MemeGallery";
import Categories from "./components/Categories";
import Community from "./components/Community";
import Faq from "./components/Faq";
import Contribute from "./components/Contribute";
import Footer from "./components/Footer";
import BackToTop from "./components/BackToTop";
import StoryModal from "./components/StoryModal";
import SearchModal from "./components/SearchModal";

function scrollToHash(href: string) {
  const el = document.querySelector(href);
  el?.scrollIntoView({ behavior: "smooth" });
}

export default function Home() {
  const [activeFilter, setActiveFilter] = useState("all");
  const [openStory, setOpenStory] = useState<Story | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);

  const goFeed = useCallback(() => {
    setActiveFilter("all");
    window.setTimeout(() => scrollToHash("#feed"), 30);
  }, []);

  const goContribute = useCallback(() => scrollToHash("#contribute"), []);

  const openStoryById = useCallback((id: number) => {
    const s = stories.find((x) => x.id === id);
    if (s) setOpenStory(s);
  }, []);

  const selectCategory = useCallback((key: string) => {
    setActiveFilter(key);
    window.setTimeout(() => scrollToHash("#feed"), 30);
  }, []);

  // global shortcut ⌘K / Ctrl+K
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // FAQPage + CollectionPage structured data
  useEffect(() => {
    const scripts: { id: string; json: object }[] = [
      {
        id: "jsonld-faq",
        json: {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      },
      {
        id: "jsonld-collection",
        json: {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "55吃瓜 · 今日爆梗合集",
          description: "全网最离谱的沙雕剧情、社死现场与生活段子合集。",
          mainEntity: {
            "@type": "ItemList",
            itemListElement: stories.slice(0, 9).map((s, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: s.title,
              url: `#feed`,
            })),
          },
        },
      },
    ];
    scripts.forEach(({ id, json }) => {
      if (document.getElementById(id)) return;
      const sc = document.createElement("script");
      sc.type = "application/ld+json";
      sc.id = id;
      sc.text = JSON.stringify(json);
      document.head.appendChild(sc);
    });
    return () => {
      scripts.forEach(({ id }) => document.getElementById(id)?.remove());
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <Header onSearchOpen={() => setSearchOpen(true)} />
      <main>
        <Hero onGoContribute={goContribute} onGoFeed={goFeed} />
        <Ticker />
        <Feed activeFilter={activeFilter} onSetFilter={setActiveFilter} onOpenStory={setOpenStory} />
        <Featured />
        <QuoteWall />
        <MemeGallery />
        <Categories onSelect={selectCategory} />
        <Community onGoContribute={goContribute} />
        <Faq />
        <Contribute />
      </main>
      <Footer />
      <BackToTop />
      <StoryModal story={openStory} onClose={() => setOpenStory(null)} onOpenStory={setOpenStory} />
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} onOpenStory={openStoryById} />
    </div>
  );
}